import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Bot,

  X,
  Key,
  CreditCard,
  FileCheck,
  FileText,
  Play,
  Upload,
  HelpCircle,
  MessageCircle,
  ArrowUp,
  Facebook,
  Instagram,
  Youtube,
  Mail,
  Music,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SupportChatPopup, OPEN_SUPPORT_CHAT_EVENT } from "@/components/support/SupportChatPopup";
import { PublicHeader } from "@/components/PublicHeader";
import { traducoes } from "@/i18n/ajuda";
import { useLanguage } from "@/contexts/LanguageContext";

// Ícones dos 6 cards FAQ (títulos e descrições vêm das traduções)
const FAQ_ICONS = [FileCheck, Key, CreditCard, Play, Upload, HelpCircle];

const BRAND_PRIMARY = "#4b36cc";

type ArticleSuggestion = { id: string; title: string };
type FaqArticle = { id: string; title: string; snippet: string };

export default function Ajuda() {
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const t = traducoes[lang];
  const [busca, setBusca] = useState("");
  const [verificarFone, setVerificarFone] = useState("");
  const [verificarFeedback, setVerificarFeedback] = useState<"idle" | "ok" | "invalid">("idle");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [reclameGifFailed, setReclameGifFailed] = useState(false);
  const [articleSuggestions, setArticleSuggestions] = useState<ArticleSuggestion[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [faqArticles, setFaqArticles] = useState<FaqArticle[]>([]);

  // Buscar artigos reais para a seção de dúvidas frequentes
  useEffect(() => {
    fetch("/api/support/articles?limit=6")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setFaqArticles(Array.isArray(data) ? data : []))
      .catch(() => setFaqArticles([]));
  }, []);

  // Buscar artigos na API conforme o usuário digita (debounce)
  useEffect(() => {
    const q = busca.trim();
    if (!q) {
      setArticleSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoadingArticles(true);
      try {
        const res = await fetch(
          `/api/support/articles/search?q=${encodeURIComponent(q)}&limit=8`
        );
        if (res.ok) {
          const data = await res.json();
          setArticleSuggestions(Array.isArray(data) ? data : []);
        } else {
          setArticleSuggestions([]);
        }
      } catch {
        setArticleSuggestions([]);
      } finally {
        setLoadingArticles(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  // Tópicos: usa títulos reais dos artigos carregados + fallback para frases estáticas
  const topicSuggestions = useMemo(() => {
    if (!busca.trim()) return [];
    const q = busca.toLowerCase();
    const fromArticles = faqArticles.map((a) => a.title).filter((p) => p.toLowerCase().includes(q));
    const fromStatic = t.topicPhrases.filter((p: string) => p.toLowerCase().includes(q));
    const seen = new Set<string>();
    return [...fromArticles, ...fromStatic]
      .filter((p) => { const k = p.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 6);
  }, [busca, faqArticles, t.topicPhrases]);

  const hasSuggestions =
    topicSuggestions.length > 0 || articleSuggestions.length > 0 || loadingArticles;

  // Placeholder animado (digitando e apagando) — títulos reais ou frases estáticas
  const placeholderPhrases = useMemo(() => {
    if (faqArticles.length >= 3) return faqArticles.slice(0, 4).map((a) => a.title);
    return t.placeholderPhrases as unknown as string[];
  }, [faqArticles, t.placeholderPhrases]);

  useEffect(() => {
    const phrases = placeholderPhrases;
    const phrase = phrases[placeholderIndex];
    const isPause = !isDeleting && charIndex === phrase.length;
    const timeout = isDeleting ? 50 : 80;
    const delay = isPause ? 1500 : isDeleting && charIndex === 0 ? 400 : timeout;
    const id = setTimeout(() => {
      if (isDeleting) {
        if (charIndex === 0) {
          setIsDeleting(false);
          setPlaceholderIndex((i) => (i + 1) % phrases.length);
        } else setCharIndex((c) => c - 1);
      } else {
        if (charIndex >= phrase.length) setIsDeleting(true);
        else setCharIndex((c) => c + 1);
      }
    }, delay);
    return () => clearTimeout(id);
  }, [placeholderIndex, charIndex, isDeleting, placeholderPhrases]);

  const animatedPlaceholder =
    placeholderPhrases[placeholderIndex % placeholderPhrases.length].slice(0, charIndex) + (isDeleting ? "" : "|");


  const handleSearch = () => {
    const q = busca.trim();
    if (q) navigate(`/search?query=${encodeURIComponent(q)}`);
  };

  const handleVerificarNumero = () => {
    const digits = verificarFone.replace(/\D/g, "");
    if (digits.length >= 10 && digits.length <= 13) {
      setVerificarFeedback("ok");
    } else {
      setVerificarFeedback("invalid");
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div
      className="min-h-screen flex flex-col text-[#3c3939] antialiased bg-white"
      style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}
    >
      <PublicHeader />

      {/* ─── Hero: Como podemos ajudar? + busca + Buscar ─── */}
      <section className="bg-white px-4 sm:px-6 pt-24 sm:pt-32 pb-0 relative overflow-visible">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h1 className="text-2xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Montserrat', system-ui, sans-serif", color: "#4b36cc" }}>
            {t.hero.title}
          </h1>
          <div className="flex flex-col mb-4 relative w-full max-w-xl mx-auto pb-20 sm:pb-24">
            <div className="relative flex-1 w-full z-10 overflow-visible">
              <div className="flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary w-full">
                <Input
                  type="text"
                  role="search"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onFocus={() => setShowAutocomplete(true)}
                  onBlur={() => setTimeout(() => setShowAutocomplete(false), 150)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  placeholder={animatedPlaceholder}
                  className="pl-4 h-12 border-0 border-r-0 outline-none shadow-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-0 bg-transparent text-[#3c3939] placeholder:text-gray-400 text-base flex-1 min-w-0 rounded-none rounded-r-none -mr-px"
                  style={{
                    paddingRight: busca.trim()
                      ? "2.5rem"
                      : "2.5rem",
                  }}
                  aria-label={t.hero.searchAriaLabel}
                  aria-autocomplete="list"
                  aria-expanded={showAutocomplete}
                />
                <div className="flex items-center shrink-0 h-12 pr-3 gap-2">
                  {busca.trim() && (
                    <button
                      type="button"
                      onClick={() => setBusca("")}
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 border-0 bg-transparent text-gray-500 hover:text-[#3c3939] hover:bg-gray-100 transition-colors focus:outline-none focus:ring-0"
                      title={t.hero.clearTitle}
                      aria-label={t.hero.clearAriaLabel}
                    >
                      <X className="h-5 w-5" aria-hidden />
                    </button>
                  )}
                  {busca.trim() && (
                    <button
                      type="button"
                      onClick={handleSearch}
                      className="h-9 px-4 rounded-lg flex items-center gap-2 text-white text-sm font-medium shrink-0 transition-all duration-300 ease-out"
                      style={{
                        background: "linear-gradient(135deg, #4b36cc 0%, #7f83fa 50%, #446eff 100%)",
                      }}
                    >
                      <Bot className="h-4 w-4 flex-shrink-0" aria-hidden />
                      <span className="whitespace-nowrap">{t.hero.searchButton}</span>
                    </button>
                  )}
                </div>
              </div>
              {/* Autocomplete: só aparece se houver tópicos, artigos ou busca em andamento */}
              {showAutocomplete && (topicSuggestions.length > 0 || articleSuggestions.length > 0 || loadingArticles) && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-xl border border-gray-200 bg-white shadow-lg py-2 z-[100] max-h-80 overflow-auto">
                  {loadingArticles && (
                    <p className="px-4 py-3 text-sm text-gray-500">{t.hero.searchingArticles}</p>
                  )}
                  {!loadingArticles && (
                    <>
                      {topicSuggestions.length > 0 && (
                        <div className="px-3 py-1.5">
                          {topicSuggestions.map((item, i) => (
                            <button
                              key={`t-${i}`}
                              type="button"
                              className="w-full text-left px-3 py-2 rounded-lg text-sm sm:text-base text-[#3c3939] hover:bg-gray-50 flex items-center gap-2"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setBusca(item);
                                setShowAutocomplete(false);
                              }}
                            >
                              <Search className="h-4 w-4 text-gray-400 shrink-0" />
                              {item}
                            </button>
                          ))}
                        </div>
                      )}
                      {articleSuggestions.length > 0 && (
                        <div className="px-3 py-1.5 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t.hero.articles}</p>
                          {articleSuggestions.map((art) => (
                            <Link
                              key={art.id}
                              to={`/artigo/${art.id}`}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm sm:text-base text-[#3c3939] hover:bg-gray-50 no-underline"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setShowAutocomplete(false)}
                            >
                              <FileText className="h-4 w-4 shrink-0" style={{ color: BRAND_PRIMARY }} />
                              {art.title}
                            </Link>
                          ))}
                        </div>
                      )}
                      {!hasSuggestions && (
                        <p className="px-4 py-3 text-sm text-gray-500">{t.hero.typeToSee}</p>
                      )}
                    </>
                  )}
                </div>
              )}
              {/* 3 bonequinhos: balanço suave esquerda/direita, borda de cima encosta na barra */}
              <div className="absolute left-1/2 top-full bonecos-swing w-[115%] max-w-[calc(100%+60px)] pointer-events-none z-0 flex justify-center">
                <img
                  src="/bonequinhos-busca.png"
                  alt=""
                  className="w-full h-auto object-contain object-top select-none"
                  style={{ mixBlendMode: "lighten", maxHeight: "280px" }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Dúvidas frequentes — fundo branco, largura total da tela ─── */}
      <section id="duvidas-frequentes" className="bg-white w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="w-full mx-auto">
          <h2 className="text-xl sm:text-2xl font-semibold mb-8 flex items-center gap-2" style={{ color: '#3c3939' }}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded" style={{ backgroundColor: `${BRAND_PRIMARY}18`, color: BRAND_PRIMARY }}>
              <FileCheck className="h-4 w-4" />
            </span>
            {t.faq.sectionTitle}
          </h2>
          {faqArticles.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {faqArticles.map((art, i) => (
                <Link
                  key={art.id}
                  to={`/artigo/${art.id}`}
                  className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col text-left no-underline"
                >
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center mb-4 shrink-0"
                    style={{ backgroundColor: `${BRAND_PRIMARY}18`, color: BRAND_PRIMARY }}>
                    {(() => { const Icon = FAQ_ICONS[i % FAQ_ICONS.length]; return <Icon className="h-6 w-6" />; })()}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#3c3939] mb-2">{art.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 flex-1">{art.snippet}</p>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">{t.faq.noArticles}</p>
          )}
        </div>
      </section>

      {/* ─── Banner FAQ: tamanho da seção inalterado; imagem cobre a área ao lado inteira sem cortar ─── */}
      <section className="relative w-full">
        <div
          className="w-full py-1 sm:py-1.5 flex flex-col sm:flex-row items-stretch justify-center gap-4 sm:gap-5 transition-opacity duration-300"
          style={{
            backgroundColor: BRAND_PRIMARY,
            clipPath: "polygon(0 0, calc(50% - 18px) 0, 50% 10px, calc(50% + 18px) 0, 100% 0, 100% 100%, 0 100%)",
          }}
        >
          <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 min-h-0">
            <div className="flex-1 max-w-2xl flex flex-col justify-center items-start text-left min-w-0">
              <div className="flex flex-wrap items-baseline justify-start gap-x-2 gap-y-1 mb-3">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-wide">
                  {t.faqBanner.title}
                </h2>
                <p className="text-white/95 text-xl sm:text-2xl max-w-xl">
                  {t.faqBanner.description}
                </p>
              </div>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent(OPEN_SUPPORT_CHAT_EVENT))}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 border-[#25D366] text-white text-xl sm:text-2xl transition-colors hover:opacity-95 w-fit"
                style={{ backgroundColor: "#25D366" }}
              >
                {t.faqBanner.cta}
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 min-w-0 min-h-[120px] sm:min-h-0 flex items-center justify-center">
              <img
                src="/faq-imagem.png"
                alt=""
                className="max-w-full max-h-full w-auto h-full object-contain object-center"
                aria-hidden
              />
            </div>
          </div>
        </div>
        {/* Seta colorida: toda para fora, colada no fim do banner */}
        <div
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            top: "100%",
            width: 0,
            height: 0,
            borderLeft: "18px solid transparent",
            borderRight: "18px solid transparent",
            borderTop: `10px solid ${BRAND_PRIMARY}`,
          }}
          aria-hidden
        />
      </section>

      {/* ─── Por que a RTG é tão confiável? — Reclame Aqui e GPTW lado a lado (quase largura total) ─── */}
      <section className="bg-white px-4 sm:px-6 pt-12 sm:pt-16 pb-2 sm:pb-4 -mt-[10px]">
        <div className="w-full max-w-[1920px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 sm:mb-14" style={{ color: "#3c3939" }}>
            {t.trust.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Card Reclame Aqui — borda e sombra como dúvidas frequentes */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              {reclameGifFailed ? (
                <div className="flex flex-col sm:flex-row min-h-[160px]">
                  <div
                    className="flex-1 flex items-center gap-4 p-5 sm:p-6"
                    style={{ backgroundColor: "#f3f4f6" }}
                  >
                    <div className="h-12 w-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${BRAND_PRIMARY}20`, color: BRAND_PRIMARY }}>
                      <FileCheck className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-bold text-[#3c3939]">{t.reclame.fallbackTitle}</p>
                      <p className="text-xs sm:text-sm text-gray-700 mt-1">
                        {t.reclame.fallbackDescription}
                      </p>
                    </div>
                  </div>
                  <div
                    className="flex flex-col items-center justify-center text-white px-5 py-4 sm:py-6"
                    style={{ backgroundColor: BRAND_PRIMARY }}
                  >
                    <span className="text-4xl mb-1" role="img" aria-hidden>😊</span>
                    <span className="text-xl sm:text-2xl font-bold">{t.reclame.fallbackGreat}</span>
                  </div>
                </div>
              ) : (
                <img
                  src="/reclame-aqui.gif"
                  alt={t.reclame.imgAlt}
                  className="w-full h-auto block object-contain object-center"
                  decoding="async"
                  fetchPriority="high"
                  onError={() => setReclameGifFailed(true)}
                />
              )}
            </div>
            {/* Card GPTW — mesma estética de card */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <img
                src="/gptw-banner.png"
                alt={t.gptw.imgAlt}
                className="w-full h-auto block object-contain object-center"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Verifique o número do nosso time (fundo branco) ─── */}
      <section className="px-4 sm:px-6 pt-8 sm:pt-10 pb-12 sm:pb-14 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-normal text-gray-900 mb-2">
            {t.verificar.title}
          </h2>
          <p className="text-[#3c3939] text-base mb-6">
            {t.verificar.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
            <div className="relative flex-1 max-w-xs mx-auto sm:mx-0">
              <MessageCircle
                className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5"
              style={{ color: BRAND_PRIMARY }}
                aria-hidden
              />
              <Input
                type="tel"
                value={verificarFone}
                onChange={(e) => {
                  setVerificarFone(e.target.value);
                  setVerificarFeedback("idle");
                }}
                placeholder={t.verificar.placeholder}
                className="pl-10 flex-1 w-full h-11 rounded-lg border border-gray-200 bg-white text-[#3c3939] placeholder:text-gray-400"
              />
            </div>
            <Button
              onClick={handleVerificarNumero}
              className="h-11 px-6 rounded-lg font-medium shrink-0 text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
            >
              {t.verificar.button}
            </Button>
          </div>
          {verificarFeedback === "ok" && (
            <p className="mt-3 text-sm font-medium"
              style={{ color: BRAND_PRIMARY }}>
              {t.verificar.success}
            </p>
          )}
          {verificarFeedback === "invalid" && (
            <p className="mt-3 text-sm text-[#3c3939] font-medium">
              {t.verificar.error}
            </p>
          )}
        </div>
      </section>

      {/* ─── Rodapé (fundo escuro) ─── */}
      <footer className="mt-auto px-4 sm:px-6 py-12 sm:py-16 text-white bg-gray-900">
        <div className="max-w-6xl mx-auto">
          {/* Ícones obrigatórios: Instagram, Facebook, YouTube, Newsletter, Spotify */}
          <div className="flex flex-wrap items-center justify-end gap-4 mb-10">
            <a
              href="#"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="#"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href="#"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
              aria-label="YouTube"
            >
              <Youtube className="h-5 w-5" />
            </a>
            <a
              href="#"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
              aria-label="Newsletter"
            >
              <Mail className="h-5 w-5" />
            </a>
            <a
              href="#"
              className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: BRAND_PRIMARY }}
              aria-label="Spotify"
            >
              <Music className="h-5 w-5" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-base font-semibold text-white mb-4">{t.footer.institucional}</h4>
              <ul className="space-y-2 text-sm sm:text-base text-white/80">
                {t.footer.institucionalLinks.map((item, i) => (
                  <li key={i}>
                    <a href="#" className="hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-4">{t.footer.nossosProdutos}</h4>
              <ul className="space-y-2 text-sm sm:text-base text-white/80">
                {t.footer.produtosLinks.map((item, i) => (
                  <li key={i}>
                    <a href="#" className="hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-4">{t.footer.nossosEventos}</h4>
              <ul className="space-y-2 text-sm sm:text-base text-white/80">
                {t.footer.eventosLinks.map((item, i) => (
                  <li key={i}>
                    <a href="#" className="hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold text-white mb-4">{t.footer.transparencia}</h4>
              <ul className="space-y-2 text-sm sm:text-base text-white/80">
                {t.footer.transparenciaLinks.map((item, i) => (
                  <li key={i}>
                    <a href="#" className="hover:text-white transition-colors">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/20">
            <div className="flex items-center gap-3">
              <img
                src="/logo-rtg.png"
                alt="RTG"
                className="h-8 w-auto object-contain"
              />
              <p className="text-sm sm:text-base text-white/70">
                {t.footer.copyright}{" "}
                <a href="mailto:suporte@readytogo.com.br" className="hover:text-white transition-colors">suporte@readytogo.com.br</a>
              </p>
            </div>
            <button
              onClick={scrollToTop}
              className="flex items-center justify-center h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white shrink-0"
              aria-label={t.footer.backToTop}
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          </div>
        </div>
      </footer>

      <SupportChatPopup />
    </div>
  );
}
